import {
  Box,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  TextInput,
  Text,
  SegmentedControlProps,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import React from 'react';

import {
  useActiveConnectionsResponse,
  useCreateOrganization,
  UseCreateOrganizationParams,
  useIsChannelPartner,
} from '@portals/api/partners';
import { ModalProps, useHasSupportSeat } from '@portals/framework';

interface AddOrganizationProps extends ModalProps {}

type CustomerAdmin = 'customer' | 'you' | 'both';

const segmentedControlData: SegmentedControlProps['data'] = [
  { value: 'customer', label: 'The customer' },
  { value: 'you', label: 'You' },
  { value: 'both', label: 'Both you and the customer' },
];

export function AddOrganization({ closeMe }: AddOrganizationProps) {
  const hasSupportSeat = useHasSupportSeat();

  const createOrganization = useCreateOrganization();

  const isChannelPartner = useIsChannelPartner();

  const activeConnectionsResponse = useActiveConnectionsResponse({
    queryOptions: {
      enabled: isChannelPartner,
    },
  });

  const activeConnectionsSelectOptions =
    activeConnectionsResponse.data?.data?.map((item) => {
      return {
        value: item.partner_id,
        label: item.display_name,
      };
    }) || [];

  const form = useForm({
    initialValues: {
      organization_name: '',
      name: '',
      email: '',
      main_partner_id: '',
      auto_admin: true,
      choose_admin: isChannelPartner ? 'both' : 'customer',
    },
  });

  const onSetCustomerAdmin = (value: CustomerAdmin) => {
    form.setFieldValue('choose_admin', value);

    if (!hasSupportSeat) {
      return;
    }

    if (value === 'customer') {
      form.setFieldValue('auto_admin', false);
      return;
    }

    form.setFieldValue('auto_admin', true);
  };

  const handleSubmit = async (data: UseCreateOrganizationParams) => {
    try {
      await createOrganization.mutateAsync(data);

      closeMe();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal opened onClose={closeMe} title="Create Customer">
      <LoadingOverlay
        visible={
          activeConnectionsResponse.isFetching || createOrganization.isLoading
        }
      />

      <Box p="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            {isChannelPartner && activeConnectionsResponse.isFetched && (
              <Select
                data={activeConnectionsSelectOptions}
                required
                placeholder="Connected Partners"
                label="Connected Partners"
                {...form.getInputProps('main_partner_id')}
              />
            )}

            <TextInput
              required
              label="Customer organization name"
              placeholder="Customer organization name"
              {...form.getInputProps('organization_name')}
            />

            <Stack>
              <Text>Choose the Admin for the Customer's Portal</Text>

              <SegmentedControl
                data={segmentedControlData}
                value={form.values.choose_admin}
                onChange={onSetCustomerAdmin}
              />
            </Stack>

            {form.values.choose_admin !== 'you' ? (
              <>
                <TextInput
                  required
                  label="Customer admin name"
                  placeholder="Customer admin name"
                  {...form.getInputProps('name')}
                />

                <TextInput
                  required
                  label="Customer admin email"
                  placeholder="Customer admin email"
                  {...form.getInputProps('email')}
                />
              </>
            ) : null}
          </Stack>

          <Divider my="lg" />

          <Group position="right">
            <Button variant="default" onClick={closeMe}>
              Close
            </Button>

            <Button type="submit">Create</Button>
          </Group>
        </form>
      </Box>
    </Modal>
  );
}
